import React, { useState, useEffect } from "react";
import "../../../css/CRUDModal.css";
import { apiList } from "../../../helpers/apiList";
import CustomAlert from "../../../helpers/CustomAlert";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import AddVectorIcon from "../../../assets/Icons/Add Vector.png";
import LeftCircle from "../../../assets/Icons/Left circle B.png";
import RightCircle from "../../../assets/Icons/Right circle A.png";
import CrossIcon from "../../../assets/Icons/cross_svg.svg";
import ModalInput from "../../../components/ModalInput/ModalInput";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PercentIcon from "@mui/icons-material/Percent";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import Dropdown from "../../../components/Dropdown";

const AddStationaryModal = ({
  show,
  handleClose,
  callApi,
  editData,
  action,
}) => {
  const loggedInUserName = sessionStorage.getItem("username");

  const [formData, setFormData] = useState({
    itemCode: "",
    itemName: "",
    unitRate: "",
    gstPercent: "",
    minQuantity: "",
    subMinQuantity: "100",
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isEditMode = action === "modify";
  const isViewMode = action === "view";
  const isDeleteMode = action === "delete";
  const isReadOnly = isViewMode || isDeleteMode;

  // ================= INIT =================
  useEffect(() => {
    if (action === "add") {
      setFormData({
        itemCode: "",
        itemName: "",
        unitRate: "",
        gstPercent: "",
        minQuantity: "",
        subMinQuantity: "100",
        isActive: true,
      });
    } else if (editData) {
      setFormData({
        itemCode: editData?.itemCode || "",
        itemName: editData?.itemName || "",
        unitRate: editData?.unitRate || "",
        gstPercent: editData?.gstPercent || "",
        minQuantity: editData?.minQuantity || "",
        subMinQuantity: String(editData?.subMinQuantity ?? 100),
        isActive: editData?.isActive ? "ACTIVE" : "INACTIVE",
      });
    }
    setErrors({});
  }, [show]);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;

    let updatedValue = type === "checkbox" ? checked : value;

    if (id === "itemCode") updatedValue = value.toUpperCase().trimStart();

    setFormData((prev) => ({
      ...prev,
      [id]: updatedValue,
    }));

    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  // ================= VALIDATION =================
  const validateField = (id, rawValue) => {
    const value = rawValue?.toString().trim();

    switch (id) {
      case "itemCode":
        if (!value) return "Item Code is required";
        return "";

      case "itemName":
        if (!value) return "Item Name is required";
        if (value.length < 2) return "Minimum 2 characters required";
        return "";

      case "unitRate":
        if (!value) return "Unit Rate required";
        if (!/^\d+(\.\d{1,2})?$/.test(value))
          return "Invalid rate (max 2 decimals)";
        if (Number(value) <= 0) return "Rate must be greater than 0";
        return "";

      case "gstPercent":
        if (!value) return "GST required";
        if (isNaN(value)) return "Invalid GST";
        if (Number(value) < 0 || Number(value) > 100)
          return "GST must be 0–100";
        return "";

      case "minQuantity":
        if (!value) return "Min quantity required";
        if (!/^\d+$/.test(value)) return "Only numbers allowed";
        if (Number(value) <= 0) return "Must be greater than 0";
        return "";

      case "subMinQuantity":
        if (!value) return "Sub-franchise min quantity required";
        if (!/^\d+$/.test(value)) return "Only numbers allowed";
        if (Number(value) <= 0) return "Must be greater than 0";
        return "";

      default:
        return "";
    }
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;

    const error = validateField(id, value);

    setErrors((prev) => {
      const next = { ...prev };
      if (error) next[id] = error;
      else delete next[id];
      return next;
    });
  };

  const validate = () => {
    const newErrors = {};

    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = () => {
    handleClose();
    setErrors({});
  };

  // ================= SUBMIT =================
  const handleSubmit = async () => {
    if (!validate()) return;

    const isAdd = action === "add";
    const apiUrl = isAdd
      ? apiList.CREATE_STATIONARY_MASTER
      : apiList.MODIFY_STATIONARY_MASTER;

    const payload = isAdd
      ? {
          username: loggedInUserName,
          itemCode: formData.itemCode,
          itemName: formData.itemName,
          unitRate: Number(formData.unitRate),
          gstPercent: Number(formData.gstPercent),
          minQuantity: Number(formData.minQuantity),
          subMinQuantity: Number(formData.subMinQuantity),
        }
      : {
          username: loggedInUserName,
          id: editData?.id,
          itemName: formData.itemName,
          unitRate: Number(formData.unitRate),
          gstPercent: Number(formData.gstPercent),
          minQuantity: Number(formData.minQuantity),
          subMinQuantity: Number(formData.subMinQuantity),
          isActive: formData.isActive === "ACTIVE",
        };

    try {
      setLoading(true);
      const res = await axiosPostApi(apiUrl, payload);

      if (res?.data?.status) {
        CustomAlert({
          title: res.data.message,
          icon: "success",
        });
        callApi(1);
        handleClose();
      } else {
        CustomAlert({
          title: "Failed",
          text: res?.data?.message,
          icon: "error",
        });
      }
    } catch (err) {
      CustomAlert({
        title: "Error",
        text: "Something went wrong",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1031,
          }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content w-[500px] min-h-fit h-[241px] rounded-3xl p-6 bg-white border border-gray-200">
              <img
                src={LeftCircle}
                alt="leftImg"
                className="absolute left-0 bottom-0 w-[110px] h-[105px]"
              />
              <img
                src={RightCircle}
                alt="leftImg"
                className="absolute right-0 top-0 w-[110px] h-[105px]"
              />
              <div className="modal-header flex items-center justify-between border-b-0 mb-3">
                <div className="flex items-center">
                  <img
                    src={AddVectorIcon}
                    alt="Add Icon"
                    className="w-[43px] h-[38px] mr-2"
                  />
                  <h5 className="modal-title main-title font-medium text-lg">
                    {isEditMode ? "Edit Stationary" : isViewMode ? "View Stationary" : isDeleteMode ? "Delete Stationary" : "Add Stationary"}
                  </h5>
                </div>
                <img
                  src={CrossIcon}
                  alt="filter icon"
                  className="w-[30px] h-[30px] cursor-pointer z-20"
                  onClick={handleBack}
                />
              </div>
              <form>
                <div className="modal-body space-y-4 items-center">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Item Code */}
                    <ModalInput
                      id="itemCode"
                      label="Stationary Code"
                      value={formData.itemCode}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={errors.itemCode}
                      placeholder="Enter Stationary Code"
                      disabled={isEditMode || isReadOnly}
                      Icon={LocalOfferIcon}
                    />

                    {/* Item Name */}
                    <ModalInput
                      id="itemName"
                      label="Stationary Name"
                      value={formData.itemName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={errors.itemName}
                      placeholder="Enter Stationary Name"
                      disabled={isReadOnly}
                      Icon={Inventory2Icon}
                    />

                    {/* Unit Rate */}
                    <ModalInput
                      id="unitRate"
                      label="Unit Rate (₹)"
                      value={formData.unitRate}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={errors.unitRate}
                      placeholder="Enter Rate"
                      disabled={isReadOnly}
                      Icon={CurrencyRupeeIcon}
                      alignRight
                    />

                    {/* GST */}
                    <ModalInput
                      id="gstPercent"
                      label="GST %"
                      value={formData.gstPercent}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={errors.gstPercent}
                      placeholder="Enter GST %"
                      disabled={isReadOnly}
                      Icon={PercentIcon}
                      alignRight
                    />

                    {/* Minimum per purchase for master LMBPs / franchises */}
                    <div>
                      <ModalInput
                        id="minQuantity"
                        label="Min Qty – LMBP"
                        value={formData.minQuantity}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={errors.minQuantity}
                        placeholder="Enter min quantity"
                        disabled={isReadOnly}
                        Icon={ProductionQuantityLimitsIcon}
                        alignRight
                      />
                      <p className="text-xs text-gray-500 mt-1">Master LMBPs (franchises)</p>
                    </div>

                    {/* Minimum per purchase for sub-LMBPs / sub-franchises */}
                    <div>
                      <ModalInput
                        id="subMinQuantity"
                        label="Min Qty – Sub-LMBP"
                        value={formData.subMinQuantity}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={errors.subMinQuantity}
                        placeholder="Enter min quantity"
                        disabled={isReadOnly}
                        Icon={ProductionQuantityLimitsIcon}
                        alignRight
                      />
                      <p className="text-xs text-gray-500 mt-1">Sub-LMBPs (sub-franchises)</p>
                    </div>

                    {isEditMode && (
                      <div>
                        <Dropdown
                          label="Status"
                          value={formData.isActive}
                          onChange={(item) => {
                            setFormData((prev) => ({
                              ...prev,
                              isActive: item.value,
                            }));

                            setErrors((prev) => ({
                              ...prev,
                              isActive: "",
                            }));
                          }}
                          options={[
                            {
                              value: "ACTIVE",
                              label: "Active",
                            },
                            {
                              value: "INACTIVE",
                              label: "Inactive",
                            },
                          ]}
                          isRequired
                          error={errors.isActive}
                          disabled={isReadOnly}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-center gap-4 pt-4 border-t-0">
                  <button
                    type="button"
                    className="btn btn-back w-[133px] h-[33px] !rounded-[8px] flex items-center justify-center"
                    onClick={handleBack}
                  >
                    Back
                  </button>
                  {!isReadOnly && (action === "add" || action === "modify") && (
                    <button
                      type="button"
                      className="btn btn-submit w-[133px] h-[33px] !rounded-[8px] flex items-center justify-center"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      Submit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ================= REUSABLE INPUT =================

const Input = ({ label, id, value, onChange, error, disabled }) => (
  <div>
    <label className="text-sm font-medium">
      {label} <span className="text-red-500">*</span>
    </label>
    <input
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full border rounded-lg px-3 py-2 mt-1"
    />
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

export default AddStationaryModal;
